import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { GitHubIntegrationState, JiraIntegrationState } from '@resolve-ai/shared';
import { integrationsApi } from '../lib/integrations-api';

export interface IntegrationStatuses {
  github: GitHubIntegrationState;
  jira: JiraIntegrationState;
}

export const INTEGRATION_STATUS_QUERY_KEY = ['integration-status'] as const;

const getNotConnectedGitHubStatus = (): GitHubIntegrationState => ({ status: 'not_connected' });
const getNotConnectedJiraStatus = (): JiraIntegrationState => ({ status: 'not_connected' });

const getDefaultIntegrationStatuses = (): IntegrationStatuses => ({
  github: getNotConnectedGitHubStatus(),
  jira: getNotConnectedJiraStatus(),
});

const fetchIntegrationStatuses = async (): Promise<IntegrationStatuses> => {
  const [github, jira] = await Promise.all([
    integrationsApi.getGitHubStatus().catch(getNotConnectedGitHubStatus),
    integrationsApi.getJiraStatus().catch(getNotConnectedJiraStatus),
  ]);

  return { github, jira };
};

export function useIntegrationStatuses() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: INTEGRATION_STATUS_QUERY_KEY,
    queryFn: fetchIntegrationStatuses,
    staleTime: 1000 * 60,
  });

  const statuses = query.data ?? getDefaultIntegrationStatuses();

  const setConnectedStatus = (integration: 'github' | 'jira', connected: boolean) => {
    queryClient.setQueryData<IntegrationStatuses>(INTEGRATION_STATUS_QUERY_KEY, (current) => {
      const base = current ?? getDefaultIntegrationStatuses();
      const status = connected ? 'connected' : 'not_connected';

      if (integration === 'github') {
        return {
          ...base,
          github: { ...base.github, status },
        };
      }

      return {
        ...base,
        jira: { ...base.jira, status },
      };
    });
  };

  const invalidateIntegrationStatuses = () =>
    queryClient.invalidateQueries({ queryKey: INTEGRATION_STATUS_QUERY_KEY });

  return {
    githubStatus: statuses.github,
    jiraStatus: statuses.jira,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    refetchIntegrationStatuses: query.refetch,
    setConnectedStatus,
    invalidateIntegrationStatuses,
  };
}
